/**
 * Test wrapper to compare listAreas2 vs listAreas3 outputs
 * This file validates that listAreas3 returns exactly the same data as listAreas2
 */

import { API } from 'aws-amplify';
import { listAreas2, listAreas3, listEmployees } from '../graphql/queries';

// Cache for employee data to avoid repeated queries
let employeeCache = null;
let employeeCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Only the test cases we're focused on
const TEST_CASES = [
  {
    name: 'useAreas Hook / Store Admin',
    variables: { report: 'store-admin', role: 'Admin', user: 'grmAdmin' }
  },
  {
    name: 'R0002 Area Selector',
    variables: { report: 'r0002_area_selector' }
  },
  {
    name: 'R0003 Store Dropdown',
    variables: { report: 'r0003_store_dropdown', user: '[user]', role: 'Admin' }
  },
  {
    name: 'R0003 Training',
    variables: { report: 'store-admin', user: 'N/A', role: 'N/A' }
  }
];

/**
 * Deep comparison of two objects
 */
function deepEqual(obj1, obj2, path = '') {
  const differences = [];
  
  if (obj1 === obj2) return { equal: true, differences };
  
  if (!obj1 || !obj2) {
    differences.push(`${path}: One value is null/undefined - obj1: ${obj1}, obj2: ${obj2}`);
    return { equal: false, differences };
  }
  
  if (typeof obj1 !== typeof obj2) {
    differences.push(`${path}: Type mismatch - obj1: ${typeof obj1}, obj2: ${typeof obj2}`);
    return { equal: false, differences };
  }
  
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2)) {
      differences.push(`${path}: obj1 is array but obj2 is not`);
      return { equal: false, differences };
    }
    
    if (obj1.length !== obj2.length) {
      differences.push(`${path}: Array length mismatch - obj1: ${obj1.length}, obj2: ${obj2.length}`);
    }
    
    for (let i = 0; i < Math.max(obj1.length, obj2.length); i++) {
      const result = deepEqual(obj1[i], obj2[i], `${path}[${i}]`);
      differences.push(...result.differences);
    }
  } else if (typeof obj1 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    const allKeys = new Set([...keys1, ...keys2]);
    
    allKeys.forEach(key => {
      if (!(key in obj1)) {
        differences.push(`${path}.${key}: Missing in listAreas2`);
      } else if (!(key in obj2)) {
        differences.push(`${path}.${key}: Missing in listAreas3`);
      } else {
        const result = deepEqual(obj1[key], obj2[key], `${path}.${key}`);
        differences.push(...result.differences);
      }
    });
  } else if (obj1 !== obj2) {
    differences.push(`${path}: Value mismatch - listAreas2: ${obj1}, listAreas3: ${obj2}`);
  }
  
  return { equal: differences.length === 0, differences };
}

/**
 * Fetch all employees and cache them
 */
async function fetchAllEmployees() {
  // Check if cache is still valid
  if (employeeCache && employeeCacheTime && 
      (Date.now() - employeeCacheTime) < CACHE_DURATION) {
    return employeeCache;
  }
  
  try {
    let allEmployees = [];
    let nextToken = null;
    
    // Paginate through all employees
    do {
      const result = await API.graphql({
        query: listEmployees,
        variables: { 
          limit: 100,
          nextToken
        }
      });
      
      const items = result.data?.listEmployees?.items || [];
      allEmployees = allEmployees.concat(items);
      nextToken = result.data?.listEmployees?.nextToken;
    } while (nextToken);
    
    // Cache the results
    employeeCache = allEmployees;
    employeeCacheTime = Date.now();
    
    console.log(`Fetched and cached ${allEmployees.length} employees`);
    return allEmployees;
  } catch (error) {
    console.error('Error fetching all employees:', error.message);
    return [];
  }
}

/**
 * Check if an Employee record exists for a given GM username
 */
async function checkEmployeeRecord(gmUsername) {
  if (!gmUsername || !gmUsername.trim()) return false;
  
  try {
    // Get all employees (cached)
    const employees = await fetchAllEmployees();
    
    // Check multiple possible matches
    const found = employees.some(emp => {
      // Check email
      if (emp.email === gmUsername) return true;
      
      // Check if username matches lastName or firstName
      if (emp.lastName === gmUsername || emp.firstName === gmUsername) return true;
      
      // Check metadata.originalUserName
      if (emp.metadata) {
        try {
          const metadata = typeof emp.metadata === 'string' 
            ? JSON.parse(emp.metadata) 
            : emp.metadata;
          if (metadata?.originalUserName === gmUsername) return true;
        } catch {
          // Ignore JSON parse errors
        }
      }
      
      // Check if username is in format "firstname.lastname"
      if (gmUsername.includes('.')) {
        const [first, last] = gmUsername.split('.');
        if (emp.firstName?.toLowerCase() === first?.toLowerCase() && 
            emp.lastName?.toLowerCase() === last?.toLowerCase()) {
          return true;
        }
      }
      
      return false;
    });
    
    return found;
  } catch (error) {
    console.error(`Error checking Employee record for ${gmUsername}:`, error.message);
    // Don't throw - just return false so the check continues
    return false;
  }
}

/**
 * Extract GM usernames from a store array, handling order differences
 */
function extractGMsFromStores(stores, debug = false) {
  if (!stores || !Array.isArray(stores)) {
    if (debug) console.log('    [DEBUG] No stores array provided');
    return new Set();
  }
  
  if (debug) {
    console.log(`    [DEBUG] Processing ${stores.length} stores`);
    if (stores.length > 0) {
      console.log('    [DEBUG] First 3 stores GM data:');
      stores.slice(0, 3).forEach((store, idx) => {
        if (store) {
          console.log(`      Store ${idx}: gm =`, store.gm, `(type: ${typeof store.gm})`);
        }
      });
    }
  }
  
  const allGMs = [];
  
  stores.forEach((store, idx) => {
    if (!store || !store.gm) return;
    
    // Handle different possible GM data structures
    if (typeof store.gm === 'string') {
      allGMs.push(store.gm);
    }
    // Handle array of GMs (multiple GMs per store)
    else if (Array.isArray(store.gm)) {
      store.gm.forEach(gm => {
        if (typeof gm === 'string') {
          allGMs.push(gm);
        } else if (gm?.username) {
          allGMs.push(gm.username);
        }
      });
    }
    // Handle object format from listAreas2 (e.g., {username: 'john.doe'})
    else if (typeof store.gm === 'object' && store.gm !== null) {
      // Try different possible object structures
      if (store.gm.username) allGMs.push(store.gm.username);
      else if (store.gm.employeeNameSnapshot) allGMs.push(store.gm.employeeNameSnapshot);
      else if (store.gm.employeeData?.metadata?.originalUserName) {
        allGMs.push(store.gm.employeeData.metadata.originalUserName);
      }
      else if (store.gm.metadata?.originalUserName) {
        allGMs.push(store.gm.metadata.originalUserName);
      }
      else if (debug && idx === 0) {
        console.log('    [DEBUG] Unhandled GM object structure:', store.gm);
      }
    }
  });
  
  // Filter out null/undefined and empty strings
  const gms = allGMs.filter(gm => {
    return gm && typeof gm === 'string' && gm.trim().length > 0;
  });
  
  if (debug) {
    console.log(`    [DEBUG] Extracted ${gms.length} GMs:`, gms);
  }
  
  // Return as Set to ignore order differences
  return new Set(gms);
}

/**
 * Compare GMs between two areas with order-independent comparison
 */
async function compareGMs(area2, area3) {
  const comparison = {
    areaName: area2.areaName,
    matches: true,
    missingInArea3: [],
    extraInArea3: [],
    gmsWithoutEmployee: []
  };
  
  const gms2 = extractGMsFromStores(area2.stores);
  const gms3 = extractGMsFromStores(area3.stores);
  
  // Find GMs missing in area3
  for (const gm of gms2) {
    if (!gms3.has(gm)) {
      comparison.missingInArea3.push(gm);
      comparison.matches = false;
      
      // Check if Employee record exists
      const hasEmployee = await checkEmployeeRecord(gm);
      if (!hasEmployee) {
        comparison.gmsWithoutEmployee.push(gm);
      }
    }
  }
  
  // Find extra GMs in area3
  for (const gm of gms3) {
    if (!gms2.has(gm)) {
      comparison.extraInArea3.push(gm);
      comparison.matches = false;
    }
  }
  
  return comparison;
}

/**
 * Compare areas by ID, not by array position
 */
async function compareAreasByID(areas2, areas3) {
  const differences = [];
  
  // Create maps for O(1) lookup
  const areas2Map = new Map();
  const areas3Map = new Map();
  
  areas2?.forEach(area => areas2Map.set(area.areaId, area));
  areas3?.forEach(area => areas3Map.set(area.areaId, area));
  
  // Check for missing areas
  areas2Map.forEach((area, areaId) => {
    if (!areas3Map.has(areaId)) {
      differences.push(`Area ${areaId} (${area.areaName}) missing in listAreas3`);
    }
  });
  
  areas3Map.forEach((area, areaId) => {
    if (!areas2Map.has(areaId)) {
      differences.push(`Area ${areaId} (${area.areaName}) missing in listAreas2`);
    }
  });
  
  // Compare matching areas
  for (const [areaId, area2] of areas2Map) {
    const area3 = areas3Map.get(areaId);
    if (!area3) continue;
    
    // Compare area fields
    ['areaName', 'areaType', 'areaManager'].forEach(field => {
      if (area2[field] !== area3[field]) {
        differences.push(`Area ${areaId}.${field}: listAreas2="${area2[field]}" vs listAreas3="${area3[field]}"`);
      }
    });
    
    // Compare GMs at area level - order independent
    const gms2 = extractGMsFromStores(area2.stores);
    const gms3 = extractGMsFromStores(area3.stores);
    
    // Check if the sets are equal (same GMs, regardless of order)
    const gms2Array = Array.from(gms2);
    const gms3Array = Array.from(gms3);
    
    const missingInArea3 = gms2Array.filter(gm => !gms3.has(gm));
    const extraInArea3 = gms3Array.filter(gm => !gms2.has(gm));
    
    // Only report if there are actual GM differences, not just order differences
    for (const gm of missingInArea3) {
      const hasEmployee = await checkEmployeeRecord(gm);
      if (!hasEmployee) {
        differences.push(`Area ${areaId}: GM "${gm}" in listAreas2 has NO Employee record ❗`);
      } else {
        differences.push(`Area ${areaId}: GM "${gm}" missing in listAreas3 (has Employee record)`);
      }
    }
    
    for (const gm of extraInArea3) {
      differences.push(`Area ${areaId}: Extra GM "${gm}" in listAreas3`);
    }
    
    // Compare stores by shortName (since storeId might differ between systems)
    const stores2Map = new Map();
    const stores3Map = new Map();
    
    area2.stores?.forEach(store => stores2Map.set(store.shortName, store));
    area3.stores?.forEach(store => stores3Map.set(store.shortName, store));
    
    // Check for missing stores
    stores2Map.forEach((store, shortName) => {
      if (!stores3Map.has(shortName)) {
        differences.push(`Store "${shortName}" missing in listAreas3 for area ${areaId}`);
      }
    });
    
    stores3Map.forEach((store, shortName) => {
      if (!stores2Map.has(shortName)) {
        differences.push(`Store "${shortName}" missing in listAreas2 for area ${areaId}`);
      }
    });
    
    // Compare matching stores
    stores2Map.forEach((store2, shortName) => {
      const store3 = stores3Map.get(shortName);
      if (!store3) return;
      
      // Store IDs are expected to differ - don't log this
      
      // Compare store fields (excluding storeId and gm which needs special handling)
      ['longName', 'toastId', 'status'].forEach(field => {
        if (store2[field] !== store3[field]) {
          differences.push(`Store "${shortName}".${field}: listAreas2="${store2[field]}" vs listAreas3="${store3[field]}"`);
        }
      });
      
      // Special handling for GM field - we don't care about the actual value matching,
      // just that both have a GM or both don't have a GM
      // Handle different GM formats (string vs object vs array)
      let hasGM2 = false;
      let hasGM3 = false;
      
      if (store2.gm) {
        if (typeof store2.gm === 'string') {
          hasGM2 = store2.gm.trim() !== '';
        } else if (Array.isArray(store2.gm)) {
          hasGM2 = store2.gm.length > 0 && store2.gm.some(gm => 
            (typeof gm === 'string' && gm.trim()) || gm?.username
          );
        } else if (typeof store2.gm === 'object') {
          // Handle object format (e.g., {username: 'john.doe'})
          hasGM2 = !!(store2.gm.username || store2.gm.employeeNameSnapshot || 
                     store2.gm.employeeData?.metadata?.originalUserName);
        }
      }
      
      if (store3.gm) {
        if (typeof store3.gm === 'string') {
          hasGM3 = store3.gm.trim() !== '';
        } else if (Array.isArray(store3.gm)) {
          hasGM3 = store3.gm.length > 0 && store3.gm.some(gm => 
            (typeof gm === 'string' && gm.trim()) || gm?.username
          );
        } else if (typeof store3.gm === 'object') {
          hasGM3 = !!(store3.gm.username || store3.gm.employeeNameSnapshot || 
                     store3.gm.employeeData?.metadata?.originalUserName);
        }
      }
      
      if (hasGM2 && !hasGM3) {
        differences.push(`Store "${shortName}".gm: Has GM in listAreas2 but not in listAreas3`);
      } else if (!hasGM2 && hasGM3) {
        differences.push(`Store "${shortName}".gm: Has GM in listAreas3 but not in listAreas2`);
      }
      
      
      // Compare labor hours
      ['fohBaseTheoHrs', 'bohBaseTheoHrs'].forEach(laborField => {
        const labor2 = store2[laborField];
        const labor3 = store3[laborField];
        
        if (!labor2 && !labor3) return;
        
        if (!labor2 || !labor3) {
          differences.push(`Store "${shortName}".${laborField}: One is missing - listAreas2=${!!labor2} vs listAreas3=${!!labor3}`);
          return;
        }
        
        ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach(day => {
          if (labor2[day] !== labor3[day]) {
            differences.push(`Store "${shortName}".${laborField}.${day}: listAreas2=${labor2[day]} vs listAreas3=${labor3[day]}`);
          }
        });
      });
    });
  }
  
  return differences;
}

/**
 * Compare single test case with performance timing
 */
async function compareQueries(variables, testName) {
  console.log(`\n🔍 Testing: ${testName}`);
  console.log('Variables:', variables);

  try {
    // Run both queries with timing
    const start2 = performance.now();
    const result2 = await API.graphql({ query: listAreas2, variables });
    const time2 = performance.now() - start2;

    const start3 = performance.now();
    const result3 = await API.graphql({ query: listAreas3, variables });
    const time3 = performance.now() - start3;

    const data2 = result2.data.listAreas2;
    const data3 = result3.data.listAreas3;
    
    // Basic statistics with performance metrics
    const stats = {
      listAreas2: {
        areaCount: data2?.length || 0,
        totalStores: data2?.reduce((sum, area) => sum + (area.stores?.length || 0), 0) || 0,
        hasLaborData: data2?.[0]?.stores?.[0]?.fohBaseTheoHrs ? true : false,
        responseTime: Math.round(time2)
      },
      listAreas3: {
        areaCount: data3?.length || 0,
        totalStores: data3?.reduce((sum, area) => sum + (area.stores?.length || 0), 0) || 0,
        hasLaborData: data3?.[0]?.stores?.[0]?.fohBaseTheoHrs ? true : false,
        responseTime: Math.round(time3)
      },
      performance: {
        listAreas2: `${Math.round(time2)}ms`,
        listAreas3: `${Math.round(time3)}ms`,
        speedup: time2 > time3 ? `${(time2 / time3).toFixed(2)}x faster` : `${(time3 / time2).toFixed(2)}x slower`
      },
      objects: {
        listAreas2: data2,
        listAreas3: data3
      }
    };
    
    // Log simplified summary
    console.log('\n📊 Performance Summary:');
    console.log(`listAreas2: ${stats.performance.listAreas2}`);
    console.log(`listAreas3: ${stats.performance.listAreas3}`);
    console.log(`Speed comparison: ${stats.performance.speedup}`);

    console.log('\n📋 Data Summary:');
    console.log('listAreas2:', {
      areas: stats.listAreas2.areaCount,
      stores: stats.listAreas2.totalStores,
      hasLabor: stats.listAreas2.hasLaborData
    });
    console.log('listAreas3:', {
      areas: stats.listAreas3.areaCount,
      stores: stats.listAreas3.totalStores,
      hasLabor: stats.listAreas3.hasLaborData
    });

    // Log the full objects for inspection
    console.log('\n📦 Full Response Objects:');
    console.log('listAreas2:', data2);
    console.log('listAreas3:', data3);

    return {
      testName,
      variables,
      match: true, // We're not checking for matches anymore
      differenceCount: 0,
      differences: [],  // No need to track differences
      stats
    };
    
  } catch (error) {
    // Better error handling to catch all types of errors
    const errorMessage = error?.message || error?.errors?.[0]?.message || JSON.stringify(error) || 'Unknown error';
    console.error(`❌ Error running test:`, error);
    console.error(`Error message: ${errorMessage}`);
    
    // Check if it's a GraphQL error
    if (error?.errors) {
      console.error('GraphQL errors:', error.errors);
    }
    
    return {
      testName,
      variables,
      error: errorMessage,
      match: false
    };
  }
}

/**
 * Run all tests with actual user/role values
 */
export async function runAllTests(username = 'testUser', role = 'Admin') {
  console.log('================================================');
  console.log('🧪 TESTING listAreas2 vs listAreas3 Compatibility');
  console.log('================================================');
  
  const results = [];
  
  for (const testCase of TEST_CASES) {
    // Replace placeholders with actual values
    const variables = { ...testCase.variables };
    if (variables.user === '[username]') variables.user = username;
    if (variables.user === '[user]') variables.user = username;
    if (variables.role === '[role]') variables.role = role;
    
    const result = await compareQueries(variables, testCase.name);
    results.push(result);
    
    // Add a small delay between tests to avoid rate limiting
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  // Summary
  console.log('\n================================================');
  console.log('📊 TEST SUMMARY');
  console.log('================================================');
  
  const passed = results.filter(r => r.match && !r.error).length;
  const failed = results.filter(r => !r.match && !r.error).length;
  const errors = results.filter(r => r.error).length;
  
  console.log(`✅ Passed: ${passed}/${results.length}`);
  console.log(`❌ Failed: ${failed}/${results.length}`);
  if (errors > 0) console.log(`⚠️ Errors: ${errors}/${results.length}`);
  
  console.log('\nDetailed Results:');
  results.forEach(r => {
    const status = r.error ? '⚠️' : (r.match ? '✅' : '❌');
    const message = r.error || (r.match ? 'MATCH' : `${r.differenceCount} differences`);
    console.log(`${status} ${r.testName}: ${message}`);
  });
  
  return results;
}

/**
 * Run a single test with custom variables
 */
export async function runSingleTest(variables, testName = 'Custom Test') {
  return compareQueries(variables, testName);
}

/**
 * Quick test with default values
 */
export async function quickTest() {
  return runSingleTest(
    { report: 'store-admin', role: 'Admin', user: 'grmAdmin' },
    'Quick Test - Store Admin'
  );
}

/**
 * Run GM validation test
 */
export async function runGMValidation() {
  console.log('='.repeat(80));
  console.log('🔍 GM VALIDATION - listAreas2 vs listAreas3');
  console.log('='.repeat(80));
  console.log('Validation Rules:');
  console.log('  ✓ Store IDs are expected to differ (not checked)');
  console.log('  ✓ GM order differences are ignored');
  console.log('  ✓ Missing GMs checked against Employee records');
  console.log('='.repeat(80));
  
  const variables = { 
    report: 'store-admin', 
    role: 'Admin', 
    user: 'grmAdmin' 
  };
  
  try {
    // Run both queries
    const [result2, result3] = await Promise.all([
      API.graphql({ query: listAreas2, variables }),
      API.graphql({ query: listAreas3, variables })
    ]);
    
    const areas2 = result2.data.listAreas2;
    const areas3 = result3.data.listAreas3;
    
    console.log(`\n📊 Data Summary:`);
    console.log(`  listAreas2: ${areas2?.length || 0} areas`);
    console.log(`  listAreas3: ${areas3?.length || 0} areas`);
    
    // Create lookup maps
    const areas2Map = new Map();
    const areas3Map = new Map();
    
    areas2?.forEach(area => areas2Map.set(area.areaName, area));
    areas3?.forEach(area => areas3Map.set(area.areaName || area.area, area));
    
    // Tracking
    const report = {
      totalAreas: areas2Map.size,
      matchedAreas: 0,
      perfectGMMatches: 0,
      gmMismatches: [],
      criticalIssues: []
    };
    
    // Process each area
    console.log('\n' + '-'.repeat(80));
    for (const [areaName, area2] of areas2Map) {
      const area3 = areas3Map.get(areaName);
      
      if (!area3) {
        console.log(`❌ Area "${areaName}" not found in listAreas3`);
        continue;
      }
      
      report.matchedAreas++;
      
      // Compare GMs with order independence and Employee validation
      const gmComparison = await compareGMs(area2, area3);
      
      if (gmComparison.matches) {
        report.perfectGMMatches++;
        console.log(`✅ ${areaName}: All GMs match`);
      } else {
        report.gmMismatches.push(gmComparison);
        console.log(`⚠️  ${areaName}: GM differences detected`);
        
        if (gmComparison.missingInArea3.length > 0) {
          console.log(`    Missing in listAreas3:`);
          for (const gm of gmComparison.missingInArea3) {
            const hasEmployee = !gmComparison.gmsWithoutEmployee.includes(gm);
            if (hasEmployee) {
              console.log(`      • ${gm} ✓ (Employee record exists)`);
            } else {
              console.log(`      • ${gm} ❗ (No Employee record)`);
              report.criticalIssues.push({ area: areaName, gm });
            }
          }
        }
        
        if (gmComparison.extraInArea3.length > 0) {
          console.log(`    Extra in listAreas3: ${gmComparison.extraInArea3.join(', ')}`);
        }
      }
    }
    
    // Summary
    console.log('\n' + '='.repeat(80));
    console.log('📊 GM VALIDATION SUMMARY');
    console.log('='.repeat(80));
    console.log(`Areas Analyzed: ${report.totalAreas}`);
    console.log(`Areas Found in Both: ${report.matchedAreas}`);
    console.log(`Areas with Perfect GM Match: ${report.perfectGMMatches}`);
    console.log(`Areas with GM Differences: ${report.gmMismatches.length}`);
    
    if (report.criticalIssues.length > 0) {
      console.log('\n❗ CRITICAL: GMs WITHOUT Employee Records:');
      report.criticalIssues.forEach(issue => {
        console.log(`   • ${issue.area}: ${issue.gm}`);
      });
      console.log('\n   Action Required: Create Employee records for these GMs');
    } else {
      console.log('\n✅ All GMs have corresponding Employee records or are properly migrated');
    }
    
    return {
      testName: 'GM Validation',
      variables,
      match: report.gmMismatches.length === 0,
      differenceCount: report.gmMismatches.length,
      criticalIssues: report.criticalIssues,
      report
    };
    
  } catch (error) {
    console.error('❌ Error during GM validation:', error);
    return {
      testName: 'GM Validation',
      variables,
      error: error.message,
      match: false
    };
  }
}

/**
 * Test Edward area specifically with detailed logging
 */
export async function testEdwardArea() {
  console.log('================================================');
  console.log('🔍 EDWARD AREA COMPARISON TEST');
  console.log('================================================');
  
  const variables = { 
    report: 'store-admin', 
    role: 'Admin', 
    user: 'grmAdmin' 
  };
  
  try {
    // Run both queries in parallel
    const [result2, result3] = await Promise.all([
      API.graphql({ query: listAreas2, variables }),
      API.graphql({ query: listAreas3, variables })
    ]);
    
    const data2 = result2.data.listAreas2;
    const data3 = result3.data.listAreas3;
    
    // Find Edward area in both results
    const edwardArea2 = data2?.find(area => area.areaName === 'Edward');
    const edwardArea3 = data3?.find(area => area.areaName === 'Edward');
    
    console.log('\n📘 listAreas2 - Edward Area:');
    if (edwardArea2) {
      console.log('Full object:', JSON.stringify(edwardArea2, null, 2));
      console.log('\nSummary:');
      console.log('  - areaId:', edwardArea2.areaId);
      console.log('  - areaName:', edwardArea2.areaName);
      console.log('  - areaType:', edwardArea2.areaType);
      console.log('  - areaManager:', edwardArea2.areaManager);
      console.log('  - store count:', edwardArea2.stores?.length || 0);
      
      if (edwardArea2.stores?.length > 0) {
        console.log('\nFirst 3 stores:');
        edwardArea2.stores.slice(0, 3).forEach(store => {
          let gmValue = null;
          if (store.gm) {
            if (typeof store.gm === 'string') {
              gmValue = store.gm;
            } else if (Array.isArray(store.gm)) {
              gmValue = store.gm.join(', ');
            } else if (store.gm?.username) {
              gmValue = store.gm.username;
            } else if (store.gm?.employeeNameSnapshot) {
              gmValue = store.gm.employeeNameSnapshot;
            }
          }
          console.log(`    ${store.shortName}:`, {
            storeId: store.storeId,
            gm: gmValue,
            status: store.status,
            hasLabor: !!(store.fohBaseTheoHrs || store.bohBaseTheoHrs)
          });
        });
      }
    } else {
      console.log('  ❌ Edward area NOT FOUND in listAreas2');
    }
    
    console.log('\n📗 listAreas3 - Edward Area:');
    if (edwardArea3) {
      console.log('Full object:', edwardArea3);
      // console.log('Full object:', JSON.stringify(edwardArea3, null, 2));
      console.log('\nSummary:');
      console.log('  - areaId:', edwardArea3.areaId);
      console.log('  - areaName:', edwardArea3.areaName);
      console.log('  - areaType:', edwardArea3.areaType);
      console.log('  - areaManager:', edwardArea3.areaManager);
      console.log('  - store count:', edwardArea3.stores?.length || 0);
      
      if (edwardArea3.stores?.length > 0) {
        console.log('\nFirst 3 stores:');
        edwardArea3.stores.slice(0, 3).forEach(store => {
          let gmValue = null;
          if (store.gm) {
            if (typeof store.gm === 'string') {
              gmValue = store.gm;
            } else if (Array.isArray(store.gm)) {
              gmValue = store.gm.join(', ');
            } else if (store.gm?.username) {
              gmValue = store.gm.username;
            } else if (store.gm?.employeeNameSnapshot) {
              gmValue = store.gm.employeeNameSnapshot;
            }
          }
          console.log(`    ${store.shortName}:`, {
            storeId: store.storeId,
            gm: gmValue,
            status: store.status,
            hasLabor: !!(store.fohBaseTheoHrs || store.bohBaseTheoHrs)
          });
        });
      }
    } else {
      console.log('  ❌ Edward area NOT FOUND in listAreas3');
    }
    
    // Detailed comparison
    console.log('\n================================================');
    console.log('📊 COMPARISON RESULTS');
    console.log('================================================');
    
    if (!edwardArea2 && !edwardArea3) {
      console.log('❌ Edward area not found in either query');
      return {
        testName: 'Edward Area Comparison',
        variables,
        match: false,
        error: 'Edward area not found in either query'
      };
    }
    
    if (!edwardArea2) {
      console.log('❌ Edward area missing in listAreas2');
      return {
        testName: 'Edward Area Comparison',
        variables,
        match: false,
        error: 'Edward area missing in listAreas2'
      };
    }
    
    if (!edwardArea3) {
      console.log('❌ Edward area missing in listAreas3');
      return {
        testName: 'Edward Area Comparison',
        variables,
        match: false,
        error: 'Edward area missing in listAreas3'
      };
    }
    
    // Compare fields
    const gms2 = extractGMsFromStores(edwardArea2.stores);
    const gms3 = extractGMsFromStores(edwardArea3.stores);
    const gmsMatch = gms2.size === gms3.size && 
                     Array.from(gms2).every(gm => gms3.has(gm));
    
    const fieldComparison = {
      areaId: edwardArea2.areaId === edwardArea3.areaId,
      areaName: edwardArea2.areaName === edwardArea3.areaName,
      areaType: edwardArea2.areaType === edwardArea3.areaType,
      areaManager: edwardArea2.areaManager === edwardArea3.areaManager,
      storeCount: (edwardArea2.stores?.length || 0) === (edwardArea3.stores?.length || 0),
      gmSet: gmsMatch  // Compare GM sets, not individual assignments
    };
    
    console.log('Field Comparison:');
    Object.entries(fieldComparison).forEach(([field, match]) => {
      if (match) {
        console.log(`  ✅ ${field}: MATCH`);
      } else {
        console.log(`  ❌ ${field}: MISMATCH`);
        if (field === 'storeCount') {
          console.log(`      listAreas2: ${edwardArea2.stores?.length}`);
          console.log(`      listAreas3: ${edwardArea3.stores?.length}`);
        } else if (field === 'gmSet') {
          console.log(`      listAreas2 GMs: ${Array.from(gms2).join(', ') || 'none'}`);
          console.log(`      listAreas3 GMs: ${Array.from(gms3).join(', ') || 'none'}`);
        } else {
          console.log(`      listAreas2: ${edwardArea2[field]}`);
          console.log(`      listAreas3: ${edwardArea3[field]}`);
        }
      }
    });
    
    // Compare stores by shortName
    if (edwardArea2.stores && edwardArea3.stores) {
      const stores2Map = new Map(edwardArea2.stores.map(s => [s.shortName, s]));
      const stores3Map = new Map(edwardArea3.stores.map(s => [s.shortName, s]));
      
      const missingInAreas3 = [...stores2Map.keys()].filter(name => !stores3Map.has(name));
      const missingInAreas2 = [...stores3Map.keys()].filter(name => !stores2Map.has(name));
      
      if (missingInAreas3.length > 0) {
        console.log(`\n❌ Stores missing in listAreas3: ${missingInAreas3.join(', ')}`);
      }
      if (missingInAreas2.length > 0) {
        console.log(`\n❌ Stores missing in listAreas2: ${missingInAreas2.join(', ')}`);
      }
      
      // Check GMs at area level (order-independent)
      console.log('\n📊 GM Comparison (Order-Independent):');
      
      const gms2 = extractGMsFromStores(edwardArea2.stores, true);  // Enable debug for area2
      const gms3 = extractGMsFromStores(edwardArea3.stores);
      
      const gms2Array = Array.from(gms2);
      const gms3Array = Array.from(gms3);
      
      console.log(`  listAreas2 GMs (${gms2.size}): ${gms2Array.join(', ') || 'none'}`);
      console.log(`  listAreas3 GMs (${gms3.size}): ${gms3Array.join(', ') || 'none'}`);
      
      const missingInArea3GMs = gms2Array.filter(gm => !gms3.has(gm));
      const extraInArea3GMs = gms3Array.filter(gm => !gms2.has(gm));
      
      if (missingInArea3GMs.length === 0 && extraInArea3GMs.length === 0) {
        console.log('\n  ✅ All GMs match (order may differ)');
      } else {
        console.log('\n  ⚠️ GM differences detected:');
        
        // Check Employee records for missing GMs
        if (missingInArea3GMs.length > 0) {
          console.log('\n  Missing in listAreas3:');
          for (const gm of missingInArea3GMs) {
            const hasEmployee = await checkEmployeeRecord(gm);
            if (hasEmployee) {
              console.log(`    • ${gm} ✓ (Employee record exists)`);
            } else {
              console.log(`    • ${gm} ❗ (NO Employee record found)`);
            }
          }
        }
        
        if (extraInArea3GMs.length > 0) {
          console.log('\n  Extra in listAreas3:');
          extraInArea3GMs.forEach(gm => {
            console.log(`    • ${gm}`);
          });
        }
      }
      
      // Show per-store GM assignment for reference (first 5)
      console.log('\n📍 Store-level GM assignments (first 5):');
      [...stores2Map.keys()].slice(0, 5).forEach(shortName => {
        const store2 = stores2Map.get(shortName);
        const store3 = stores3Map.has(shortName) ? stores3Map.get(shortName) : null;
        
        // Extract GM username from different formats (including arrays)
        let gm2 = 'no GM';
        if (store2.gm) {
          if (typeof store2.gm === 'string') {
            gm2 = store2.gm;
          } else if (Array.isArray(store2.gm)) {
            gm2 = store2.gm.join(', ');
          } else if (store2.gm?.username) {
            gm2 = store2.gm.username;
          } else if (store2.gm?.employeeNameSnapshot) {
            gm2 = store2.gm.employeeNameSnapshot;
          }
        }
        
        let gm3 = 'no GM';
        if (store3 && store3.gm) {
          if (typeof store3.gm === 'string') {
            gm3 = store3.gm;
          } else if (Array.isArray(store3.gm)) {
            gm3 = store3.gm.join(', ');
          } else if (store3.gm?.username) {
            gm3 = store3.gm.username;
          } else if (store3.gm?.employeeNameSnapshot) {
            gm3 = store3.gm.employeeNameSnapshot;
          }
        }
        
        if (store3) {
          console.log(`  ${shortName}:`);
          console.log(`    listAreas2: ${gm2}`);
          console.log(`    listAreas3: ${gm3}`);
        } else {
          console.log(`  ${shortName}: Only in listAreas2 (${gm2})`);
        }
      });
      
      if (stores2Map.size > 5) {
        console.log(`  ... and ${stores2Map.size - 5} more stores`);
      }
    }
    
    const allFieldsMatch = Object.values(fieldComparison).every(v => v === true);
    
    console.log('\n' + '='.repeat(50));
    console.log(allFieldsMatch ? '✅ PERFECT MATCH!' : '❌ DIFFERENCES FOUND');
    console.log('='.repeat(50));
    
    return {
      testName: 'Edward Area Comparison',
      variables,
      match: allFieldsMatch,
      differenceCount: Object.values(fieldComparison).filter(v => !v).length,
      stats: {
        listAreas2: {
          areaCount: 1,
          totalStores: edwardArea2.stores?.length || 0,
          hasLaborData: edwardArea2.stores?.[0]?.fohBaseTheoHrs ? true : false
        },
        listAreas3: {
          areaCount: 1,
          totalStores: edwardArea3.stores?.length || 0,
          hasLaborData: edwardArea3.stores?.[0]?.fohBaseTheoHrs ? true : false
        }
      }
    };
    
  } catch (error) {
    console.error(`❌ Error running Edward area test: ${error.message}`);
    return {
      testName: 'Edward Area Comparison',
      variables,
      error: error.message,
      match: false
    };
  }
}

// Export for use in browser console
if (typeof window !== 'undefined') {
  window.testListAreas3 = {
    runAllTests,
    runSingleTest,
    quickTest,
    testEdwardArea,
    runGMValidation,
    TEST_CASES
  };
}

export default {
  runAllTests,
  runSingleTest,
  quickTest,
  testEdwardArea,
  runGMValidation,
  TEST_CASES
};